"use client";

import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { hasNoteOpenParam } from "@/lib/note-open-param";
import {
  List,
  Map as MapIcon,
  Trophy,
  ListOrdered,
  Pencil,
  Activity,
  ListChecks,
  X,
  PanelRightOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getPaymentProvider } from "@/lib/payment-provider";
import videoDockStyles from "./ArticleVideoDock.module.css";
import {
  getArticleCategoryLabel,
  getScopeForPath,
  isTheoryLearningPage,
  isRestorationPage,
  getIndexShellLabel,
  isSubjectIndexPage,
  getMyDashboardLabel,
  isMyTwoColumnPage,
  isMyDashboardPage,
  isHomeDashboardPage,
  HOME_SHELL_LABEL,
  isPassRatePage,
  isExamInfoPage,
  isApplicationPage,
  isPreApplicationPage,
  isSummaryPdfDetailPage,
  isMockExamShellPage,
  isFinalMockExamPage,
  SUMMARY_PDF_DETAIL_LABEL,
  isCommunityIssuesPage,
  COMMUNITY_ISSUES_SHELL_LABEL,
  isCommunityTalkPage,
  COMMUNITY_TALK_SHELL_LABEL,
  ERROR_NOTES_PATH,
} from "@/lib/route-layout";
import { getTrailMapLabel, TRAIL_QUERY_PARAM } from "@/lib/article-trail";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { useIsCompactShell } from "@/hooks/useViewport";
import ShellFrame, { RAIL_WIDTH } from "./ShellFrame";
import ResizeHandle from "./ResizeHandle";
import Section from "./Section";
import SolutionAside from "./SolutionAside";
import TrailMap from "./TrailMap";
import PassRateMiniChart from "./PassRateMiniChart";
import PassRateReviewCard, { ExamPreparationCard } from "./PassRateReviewCard";
import RoundStatsCard from "./RoundStatsCard";
import ChecklistMini from "./ChecklistMini";
import TestCenterBanner from "./TestCenterBanner";
import { getRoundStatsForPath } from "@/lib/pass-rate-data";
import {
  LEFT_SIDEBAR_DEFAULT_KEY,
  RIGHT_SIDEBAR_DEFAULT_KEY,
  useSidebarDefault,
} from "@/lib/sidebar-preferences";
import {
  SlotRegistryContext,
  ArticleVideoDockContext,
  SolutionModeContext,
  deriveRightColumn,
  type SlotRegistry,
  type SolutionMode,
} from "./ArticleShellContext";
import {
  clamp,
  clampRatio,
  resolveProblemSplitRatioUpdate,
  restoreSolutionSplitPreference,
  writeLS,
  useRestoreOnMount,
  capCompactWidth,
} from "./shell-primitives";

/**
 * 이론/코딩 글 페이지의 3컬럼 양쪽 사이드바 셸 (JCG-2060 토대).
 *
 * 좌: 아이콘 레일 + 콘텐츠 슬롯(ad-free → 지도(JCG-2061) + 목차(JCG-2063), free·비로그인 → 광고).
 *     합격률 페이지의 실기 합격률 추이 그래프는 콘텐츠성 위젯이라 로그인/등급 무관 노출.
 *     기출·퀴즈 페이지면 지도 자리에 점수 Section(JCG-2066) — 풀이 모드(우측 변신)에서도 좌측을 열면 정답률 노출.
 * 가운데: 슬림 헤더 + 본문(문서 스크롤 유지).
 * 우: ad-free → 필기·출석(JCG-2063) / free·비로그인 → 광고.
 *
 * 좌/우 도구 패널(목차·필기·출석)은 PostLayout(children) 안쪽에 있어, 셸이 사이드바에 둔
 * 포털 슬롯 div 를 ArticleShellContext 로 내려주면 각 컴포넌트가 createPortal 로 그린다.
 *
 * 폭은 CSS 변수 `--left-w`/`--right-w`(localStorage 영속) + 드래그 리사이즈로 관리.
 * 스크롤은 문서(window) 기준 — PostLayout 의 기존 window-scroll 컴포넌트를 무수정 보존한다.
 */
const LEFT_MIN = 320;
const LEFT_MAX = 560;
const LEFT_DEFAULT = 372;
const RIGHT_MIN = 300;
const RIGHT_MAX = 680;
const RIGHT_DEFAULT = 320;
const INDEX_CUSTOM_RIGHT_DEFAULT = 420; // 인덱스 셸(JCG-2104) 우측 기본 폭 — 미리보기가 넓게 보이도록 article 보다 큼
const MY_CUSTOM_RIGHT_DEFAULT = 360; // /my 대시보드(JCG-2073) 우측 기본 폭 — 맥락 카드가 보이도록 article 보다 약간 큼
const HOME_CUSTOM_RIGHT_DEFAULT = 360; // 홈(/) 대시보드(JCG-2110) 우측 기본 폭 — 추천 글·배너 스택이 보이도록 /my 와 동일
const ISSUE_BOARD_CUSTOM_RIGHT_DEFAULT = 360; // 질문·버그 게시판(JCG-3029) 우측 기본 폭 — 안내·글 정보·진행 이력 카드 스택(/my 와 동일)
const TALK_BOARD_CUSTOM_RIGHT_DEFAULT = 320; // 감자단 잡담(JCG-3029) 우측 기본 폭 — 글쓰기 CTA·추천글 TOP·안내 카드(표 제목 폭 확보를 위해 /my 보다 좁게)
const PDF_DETAIL_CUSTOM_RIGHT_DEFAULT = 380; // 요약 PDF 상세(JCG-2121) 우측 기본 폭 — sticky 구매 CTA(가격·결제 버튼·유튜브 인증)가 넉넉히 보이도록
// 토스 어드민에서 PC에도 모바일 2열 UI를 적용한다. 구매 패널은 기존 PDF 기본 폭을 유지한다.
const PDF_TOSS_RIGHT_MIN = PDF_DETAIL_CUSTOM_RIGHT_DEFAULT;
const SOLUTION_DEFAULT = 440; // 풀이 모드 우측 폭 기본(JCG-2065) — clamp 는 RIGHT_MIN/MAX 공유
const SOLUTION_RAIL = 52; // 풀이 "숨기기" 시 우측 레일 폭(JCG-2065)
// 오답노트 복습(JCG-2060): 풀이 없는 문제는 풀이 pane 이 placeholder 뿐이라 오답노트를 90%까지
// 키운다. 드래그 클램프도 error-notes 에선 풀이 pane 최소를 0.1(노트 최대 90%)로 완화한다
// (일반/forum 은 SPLIT_RATIO_MIN 0.22 유지 — 양쪽 pane 붕괴 방지 공유 가드).
const ERROR_NOTES_SPLIT_MIN = 0.1;
// 작성 모드(JCG-2102) — 복원 게시판 PostEditor(편집+미리보기 2컬럼)를 위해 풀이보다 넓게 확장.
const COMPOSE_MIN = 420;
const COMPOSE_DEFAULT = 720;
const COMPOSE_MAX = 960;
// RAIL_WIDTH(접힘 시 좌측 컬럼 폭)는 ShellFrame 이 단일 소유 — import 로 공유(JCG-2139).
const SPLIT_MIN = 120; // 세로 스플릿에서 각 pane 최소 높이(px)
const MAP_DEFAULT = 300; // 지도 pane 기본 높이(둘 다 펼침일 때)
const CHECKLIST_SPLIT_DEFAULT = 280; // 체크리스트 pane 기본 높이(둘 다 펼침일 때, JCG-2101)
const SPLIT_RATIO_MIN = 0.22; // 풀이/오답 50/50 분할 클램프
const SPLIT_RATIO_MAX = 0.78;
const LS_LEFT = "ui2-left-w";
const LS_RIGHT = "ui2-right-w";
const LS_CUSTOM_RIGHT = "ui2-custom-right-w"; // custom 셸 우측 폭(article 필기 폭과 분리, JCG-2104/2073)
const LS_SOLUTION = "ui2-solution-w"; // 풀이 모드 우측 폭(px)
// 오답노트 복습 전용 풀이↕오답노트 비율. 다른 article/forum 풀이와 상태를 공유하지 않으며,
// 스키마 변경 시 기존 값을 잘못 해석하지 않도록 버전을 키에 포함한다(JCG-2856).
const LS_ERROR_NOTES_SOLUTION_SPLIT = "ui2-error-notes-solution-split-v1";
const LS_COMPOSE = "ui2-compose-w"; // 작성 모드 우측 폭(px, JCG-2102)
const LS_LEFT_SPLIT = "ui2-left-split"; // 지도 pane 높이(px)
const LS_LEFT_SPLIT_CHECKLIST = "ui2-left-split-checklist"; // 체크리스트 pane 높이(px, JCG-2101)
const LS_SEC_MAP = "ui2-sec-map"; // 지도 Section 펼침
const LS_SEC_TOC = "ui2-sec-toc"; // 목차 Section 펼침
const LS_SEC_SCORE = "ui2-sec-score"; // 점수 Section 펼침(JCG-2066)
const LS_SEC_PROBNAV = "ui2-sec-probnav"; // 문제 목록 Section 펼침(JCG-2066)
const LS_SEC_PASSRATE = "ui2-sec-passrate"; // 합격률 추이 Section 펼침(JCG-2101)
const LS_SEC_CHECKLIST = "ui2-sec-checklist"; // 원서 접수 체크리스트 Section 펼침(JCG-2101)
// 파이널 모의고사(/final-mock-exam) 전용 "오팔 유리" 표면 3종(JCG-2898). 앰버·로즈·바이올렛·청록이
// 낮은 채도로 섞이는 진주빛(오팔) — 헤더·사이드바·본문 톤을 하나로 잇되 강도는 헤더 < 본문(상단만) <
// 사이드바로 차등. 멀티휴 radial 메시라 Tailwind 유틸로 표현 불가 → globals.css 유틸 클래스
// (.mock-exam-*-surface, light + .dark 페어)로 정의하고 여기선 클래스명만 참조(inline style 금지,
// ui-patterns §4-5). 카드(bg-card 불투명)는 자체 표면이라 이 배경 위에서도 대비 유지.
// isFinalMockExamPage 게이트 전용 — 일반 /mock-exam 은 아래 MOCK_EXAM_SIDEBAR_SURFACE(앰버) 유지.
const FINAL_MOCK_EXAM_SIDEBAR_SURFACE = "mock-exam-sidebar-surface"; // 좌/우 aside(파이널만)
const FINAL_MOCK_EXAM_MAIN_SURFACE = "mock-exam-main-surface"; // 본문(상단만 오팔, 아래는 화이트 페이드)
const FINAL_MOCK_EXAM_HEADER_SURFACE = "mock-exam-header-surface"; // sticky 헤더(가장 옅게, frost 보존)
// 프리미엄 모의고사(/mock-exam) 사이드바 — 기존 앰버 그라데이션(파이널의 오팔과 분리, JCG-2898).
// isMockExamShell 이지만 isFinalMockExamPage 는 아닌 경로(=/mock-exam)의 좌/우 aside 배경.
const MOCK_EXAM_SIDEBAR_SURFACE =
  "bg-gradient-to-b from-amber-100/80 via-orange-50 to-yellow-100/70 dark:from-amber-950/60 dark:via-orange-950/35 dark:to-yellow-950/30";

/** 접이식 Section 펼침 상태를 localStorage 에 영속하며 토글하는 콜백(지도/목차/점수/문제목록 공용). */
function usePersistedToggle(
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  storageKey: string,
) {
  return React.useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeLS(storageKey, String(next));
      return next;
    });
  }, [setOpen, storageKey]);
}

interface ArticleShellProps {
  children: React.ReactNode;
  /** 서버 렌더된 AdSense 슬롯 (좌 콘텐츠 / 우 상단) */
  leftAd: React.ReactNode;
  rightAd: React.ReactNode;
  /** 서버 컴포넌트 (Footer, 전역 핸들러) */
  footer: React.ReactNode;
  globals: React.ReactNode;
  /**
   * 셸 변형 (JCG-2104/2073).
   * - "article"(기본): 이론/코딩 글·기출 풀이 — 지도/목차/점수/풀이 등 학습 도구 셸.
   * - "custom": 인덱스 셸(/forum/jcg) · /my 통합 대시보드 — 좌/우 사이드바를 페이지(children)가
   *   custom 슬롯에 직접 주입.
   *   학습 도구·광고·풀이 모드 로직을 타지 않는 단순 렌더 경로.
   */
  variant?: "article" | "custom";
}

/**
 * 지도 Section 라벨("C언어 지도" 등). 공유 글의 지도 컨텍스트(`?trail=`, JCG-2487)를 읽어야 하므로
 * `useSearchParams` 가 필요하고, cacheComponents 프리렌더에서는 이 훅이 Suspense 경계를 요구한다.
 *
 * 경계는 라벨 텍스트 하나로 좁힌다(JCG-3454). 셸 전체를 경계로 감싸고 fallback 이 본문(children)을
 * 다시 그리면, 정적 셸(fallback)과 요청 시 재개된 콘텐츠에 본문이 각각 들어가 검색 봇(네이버 Yeti 등)이
 * 받는 초기 HTML 에 `<h1>` 이 2개 나온다(서치어드바이저 "H1 요소가 2개 이상 발견" 경고).
 * fallback 은 trail 쿼리 없이 pathname 만으로 계산한 라벨이라 정적 셸에서도 의미 있는 텍스트를 보인다.
 */
function MapSectionLabel() {
  return (
    <React.Suspense
      fallback={<MapSectionLabelText requestedTrailCategory={null} />}
    >
      <MapSectionLabelWithSearchParams />
    </React.Suspense>
  );
}

function MapSectionLabelWithSearchParams() {
  const searchParams = useSearchParams();
  return (
    <MapSectionLabelText
      requestedTrailCategory={searchParams.get(TRAIL_QUERY_PARAM)}
    />
  );
}

function MapSectionLabelText({
  requestedTrailCategory,
}: {
  requestedTrailCategory: string | null;
}) {
  const pathname = usePathname() ?? "";
  return getTrailMapLabel(pathname, requestedTrailCategory);
}

export default function ArticleShell({
  children,
  leftAd,
  rightAd,
  footer,
  globals,
  variant = "article",
}: ArticleShellProps) {
  const pathname = usePathname() ?? "";
  const isMockExamShell = isMockExamShellPage(pathname);
  // 파이널 모의고사(정확 일치)만 본문(main)·헤더에 오팔 배경을 칠한다(JCG-2898). 판정은 route-layout
  // SSOT(isFinalMockExamPage, normalize 경유)로 위임 — 사이드바 공유 게이트(isMockExamShell)와 패리티 유지.
  // 일반 /mock-exam 은 mainClassName/surfaceClassName 미전달(undefined)로 기존 배경 유지(회귀 방지).
  const isFinalMockExam = isFinalMockExamPage(pathname);
  // 좌/우 사이드바 표면: 파이널만 오팔, 일반 /mock-exam 은 기존 앰버(JCG-2898 분리). 좌 wrapper·우 aside 공용.
  const mockExamSidebarSurface = isFinalMockExam
    ? FINAL_MOCK_EXAM_SIDEBAR_SURFACE
    : MOCK_EXAM_SIDEBAR_SURFACE;
  // 오답노트 복습 페이지(JCG-2060) — 풀이 스크롤 추종/좌측 유지/분할 완화 분기의 단일 게이트.
  // 리사이즈 콜백·누수방어 effect·custom 렌더가 공유하므로 pathname 직후로 hoist.
  const isErrorNotesPath = pathname === ERROR_NOTES_PATH;
  const categoryLabel = getArticleCategoryLabel(pathname);
  // 브레드크럼 중간 단계(JCG-2108): subject-posts 에 등록된 글이면 사람이 읽는 scope 라벨,
  // 없으면 "" → ContentHeader 가 스코프 단계를 생략하고 기존 2단(카테고리 > 제목)을 유지.
  const scopeLabel = getScopeForPath(pathname);
  // 지도·출석은 이론 학습 글에서만(기출·모의고사·해설·학습계획·예상문제 제외, JCG-2066).
  const isLearning = isTheoryLearningPage(pathname);
  // 복원 게시판(JCG-2102): 좌/우/상단 바를 페이지가 슬롯 주입하는 포럼형 셸. 지도·목차·점수 분기 대체.
  const isRestoration = isRestorationPage(pathname);
  // 합격률 페이지는 좌측 사이드바 목차 위에 실기 합격률 추이 미니 차트를 노출(JCG-2101, 판정은 route-layout).
  const showPassRate = isPassRatePage(pathname);
  // 원서 접수 페이지는 좌측 사이드바 목차 위에 원서 접수 체크리스트를 노출(JCG-2101, 판정은 route-layout).
  const showApplication = isApplicationPage(pathname);
  // 카테고리 인덱스(`/coding/c-language` 등, JCG-2098): 좌 = 주요항목, 우 = 지도, 본문 = 글 목록.
  // 목차·필기·출석·점수가 없으므로 일반 글/문제 풀이와 다른 사이드바 분기를 탄다.
  const isSubjectIndex = isSubjectIndexPage(pathname);
  // /my 통합 대시보드(JCG-2073): custom 셸 중에서도 인덱스 셸(/forum/jcg)과 우측 기본 폭·상단바
  // 제목·본문 폭 캡이 달라 분기 기준으로 쓴다.
  const isMyDashboard = isMyDashboardPage(pathname);
  // 홈(/) 대시보드(JCG-2110): custom 셸. 우측 기본 폭·상단바 제목·본문 폭 캡 분기 기준.
  const isHomeDashboard = isHomeDashboardPage(pathname);
  // 요약 PDF 상세 랜딩(JCG-2121): custom 셸. 좌 섹션 앵커 / 우 sticky 구매 CTA. 우측 기본 폭·
  // 상단바 제목·본문 폭 캡이 다른 custom 변형이라 분기 기준으로 쓴다.
  const isPdfDetail = isSummaryPdfDetailPage(pathname);
  const isTossPdfDetail = isPdfDetail && getPaymentProvider() === "toss";
  const baseRightMin = isTossPdfDetail ? PDF_TOSS_RIGHT_MIN : RIGHT_MIN;
  // 이슈 게시판(JCG-3029): custom 셸. 좌 필터 네비 / 우 안내·이슈 정보 카드. 우측 기본 폭·상단바 제목 분기 기준.
  const isIssueBoard = isCommunityIssuesPage(pathname);
  // 감자단 잡담(JCG-3029): custom 셸. 좌 목록·정렬 네비 / 우 글쓰기 CTA·추천글 TOP·안내 카드.
  const isTalkBoard = isCommunityTalkPage(pathname);

  // 컴팩트 셸 구간(1024~1279px, JCG-2224): 셸은 유지하되 사이드바가 본문에 폭을 양보한다.
  // 구간 진입 시 좌측을 레일로 접고, 우측 폭을 캡(capRight)해 중앙 본문 폭을 확보한다.
  // 1280px↑(와이드 데스크탑)은 false 라 기존 경험 불변.
  const compact = useIsCompactShell();

  // 기출 회차 풀이/해설 페이지(`/past-exam/<YYYY>/<slug>`)면 그 회차의 합격률·난이도·코멘트(JCG-2167).
  // 우측 사이드바 하단 RoundStatsCard 로 노출. 모의고사 등 회차 개념이 없으면 null → 카드 미노출.
  const roundStats = React.useMemo(() => getRoundStatsForPath(pathname), [pathname]);
  const showExamPreparation = isExamInfoPage(pathname);

  const [leftW, setLeftW] = React.useState(LEFT_DEFAULT);
  // custom 셸 우측 기본 폭: 홈 대시보드(360)·/my 대시보드(360)·요약 PDF 상세(380, JCG-2121)·
  // 인덱스 셸(/forum/jcg, 420). 상수라 hydration mismatch 없음. 첫 방문 기본값(이후 LS_CUSTOM_RIGHT 영속).
  const customRightDefault = isHomeDashboard
    ? HOME_CUSTOM_RIGHT_DEFAULT
    : isMyDashboard
      ? MY_CUSTOM_RIGHT_DEFAULT
      : isPdfDetail
        ? PDF_DETAIL_CUSTOM_RIGHT_DEFAULT
        : isIssueBoard
          ? ISSUE_BOARD_CUSTOM_RIGHT_DEFAULT
          : isTalkBoard
            ? TALK_BOARD_CUSTOM_RIGHT_DEFAULT
            : INDEX_CUSTOM_RIGHT_DEFAULT;
  const [rightW, setRightW] = React.useState(
    variant === "custom" ? customRightDefault : RIGHT_DEFAULT,
  );
  const [leftCollapsed, setLeftCollapsed] = React.useState(false);
  const [rightCollapsed, setRightCollapsed] = React.useState(false);
  const [leftDefault] = useSidebarDefault(LEFT_SIDEBAR_DEFAULT_KEY);
  const [rightDefault, setRightDefault] = useSidebarDefault(
    RIGHT_SIDEBAR_DEFAULT_KEY,
  );
  const previousDefaults = React.useRef<{
    compact: boolean;
    left: boolean | null;
    right: boolean | null;
  } | null>(null);
  React.useEffect(() => {
    const previous = previousDefaults.current;
    if (
      !previous ||
      previous.compact !== compact ||
      previous.left !== leftDefault
    ) {
      setLeftCollapsed(leftDefault ?? compact);
    }
    if (!previous || previous.right !== rightDefault) {
      setRightCollapsed(rightDefault ?? false);
    }
    previousDefaults.current = { compact, left: leftDefault, right: rightDefault };
  }, [compact, leftDefault, rightDefault]);

  // `?open=note`(즐겨찾기 등에서 "필기 열기")로 들어오면 접어 둔 우측 사이드바를 이번만 펼친다.
  // 저장된 기본값(rightDefault)은 바꾸지 않는다. 필기 Section 펼침·포커스는 Note 가 맡는다(JCG-3000).
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (hasNoteOpenParam()) setRightCollapsed(false);
  }, [pathname]);

  // 좌측 사이드바 세로 스플릿(JCG-2063): 지도 ↕ 목차. 둘 다 펼쳐졌을 때만 핸들이 보이고,
  // mapPx 가 지도 pane 높이를 잡으면 목차 pane 이 나머지를 차지한다. 한쪽만 펼침이면 그쪽이
  // flex-1 로 전부 차지, 둘 다 접힘이면 헤더만 쌓인다. 펼침 상태/스플릿 높이 모두 localStorage 영속.
  const [mapOpen, setMapOpen] = React.useState(true);
  const [tocOpen, setTocOpen] = React.useState(true);
  const [mapPx, setMapPx] = React.useState(MAP_DEFAULT);
  const splitRef = React.useRef<HTMLDivElement>(null);

  // 합격률 추이 미니 차트 Section 펼침(JCG-2101) — 목차 위, isPassRatePage 에서만 렌더.
  const [passRateOpen, setPassRateOpen] = React.useState(true);
  // 원서 접수 체크리스트 Section 펼침(JCG-2101) — 목차 위, isApplicationPage 에서만 렌더.
  // 체크리스트 ↕ 목차 세로 스플릿(지도↕목차와 동일): checklistPx 가 체크리스트 pane 높이를 잡는다.
  const [checklistOpen, setChecklistOpen] = React.useState(true);
  const [checklistPx, setChecklistPx] = React.useState(CHECKLIST_SPLIT_DEFAULT);
  const checklistSplitRef = React.useRef<HTMLDivElement>(null);

  // 풀이 모드(JCG-2065): 우측 사이드바가 "학습 도구" ↔ "풀이"로 변신한다.
  // 좌측 메뉴 상태는 풀이 진입/종료와 분리하고, 오답노트 열림이면 풀이/오답을 세로 분할한다.
  // rightMode/wrongOpen/activeSolutionId 는 세션 상태, solutionW는 모든 풀이 경로에서 영속한다.
  // splitRatio는 오답노트 exact path에서 사용자가 핸들로 조절한 값만 별도 키에 영속(JCG-2856).
  const [rightMode, setRightMode] = React.useState<
    "panels" | "solution" | "compose"
  >("panels");
  const [solutionW, setSolutionW] = React.useState(SOLUTION_DEFAULT);
  // 작성 모드(JCG-2102) 우측 폭 — 풀이와 별도 영속(LS_COMPOSE).
  const [composeW, setComposeW] = React.useState(COMPOSE_DEFAULT);
  // 작성 모드 패널 컨테이너 ref(JCG-2123 #6) — 진입 시 포커스 이동·Escape 닫기용.
  // 트리거 버튼(RestorationTools 의 "복원 문제 작성")은 작성 모드에서 hidden 이라 포커스가 고립되므로,
  // 진입 시 패널로 포커스를 옮기고 Escape 로 닫을 수 있게 한다.
  const composePanelRef = React.useRef<HTMLDivElement>(null);
  // 작성 모드 진입 직전 포커스를 잡고 있던 트리거 요소 — 닫을 때 포커스를 되돌려준다(overlay 포커스 복원).
  const composeReturnFocusRef = React.useRef<HTMLElement | null>(null);
  // 풀이 "숨기기": 풀이 모드를 유지한 채 우측을 얇은 레일로 최소화(activeSolutionId 보존, 쉽게 재오픈).
  // "풀이 닫기"(exitSolution)와 구분 — 닫기는 학습 도구 모드로 완전 복귀.
  const [solutionCollapsed, setSolutionCollapsed] = React.useState(false);
  const [wrongOpen, setWrongOpen] = React.useState(false);
  const [splitRatio, setSplitRatio] = React.useState(0.5);
  // true면 사용자가 오답노트 분할 핸들을 조절했거나 그 값을 localStorage에서 복원한 상태다.
  // ProblemAnswer의 문제별 동적 기본값(풀이 있음 0.5 / 없음 0.1)이 이후 문제 이동에서 덮어쓰지
  // 못하게 하는 transient 가드라 렌더링과 무관하며 ref로 소유한다.
  const errorNotesSplitAdjustedRef = React.useRef(false);
  const [activeSolutionId, setActiveSolutionId] = React.useState<string | null>(
    null,
  );
  const [solutionSlot, setSolutionSlot] = React.useState<HTMLElement | null>(
    null,
  );
  const [errorNoteSlot, setErrorNoteSlot] = React.useState<HTMLElement | null>(
    null,
  );
  const solutionSplitRef = React.useRef<HTMLDivElement>(null);

  // 출석 ↔ 지도 동기화(JCG-2065): 출석 토글 시 버전을 올려 TrailMap 이 출석 표시를 재조회하게 한다.
  const [attendanceVersion, setAttendanceVersion] = React.useState(0);
  const refreshAttendance = React.useCallback(
    () => setAttendanceVersion((v) => v + 1),
    [],
  );

  // 점수 패널(JCG-2066): 기출·퀴즈 페이지에서만 ArticleScorePanel 이 마운트되며 scoreActive 를 켜
  // 좌측 사이드바(지도 자리)에 "점수" Section 을 노출한다. scoreSlot 에 ArticleScorePanel 이 점수를 portal.
  const [scoreSlot, setScoreSlot] = React.useState<HTMLElement | null>(null);
  const [scoreActive, setScoreActive] = React.useState(false);
  const [scoreOpen, setScoreOpen] = React.useState(true);

  // 문제 목록 네비(JCG-2066): 퀴즈/기출 페이지는 목차 자리에 "1·2·3…" 문제 번호 칩을 둔다.
  // ProblemListNav 가 칩을 problemNavSlot 에 portal. 퀴즈 신호는 점수와 동일하게 scoreActive 재사용.
  const [problemNavSlot, setProblemNavSlot] =
    React.useState<HTMLElement | null>(null);
  const [problemNavOpen, setProblemNavOpen] = React.useState(true);

  // 복원 게시판 슬롯(JCG-2102): 상단 바·좌/우 사이드바·작성 에디터를 RestorationPageClient 가 portal.
  const [shellHeaderSlot, setShellHeaderSlot] =
    React.useState<HTMLElement | null>(null);
  // 글 페이지 헤더 우측 액션 슬롯(JCG-2211): LanguageToggle 이 데스크탑 셸에서 본문 대신 portal.
  const [headerActionSlot, setHeaderActionSlot] =
    React.useState<HTMLElement | null>(null);
  const [restorationLeftSlot, setRestorationLeftSlot] =
    React.useState<HTMLElement | null>(null);
  const [restorationRightSlot, setRestorationRightSlot] =
    React.useState<HTMLElement | null>(null);
  const [composeSlot, setComposeSlot] = React.useState<HTMLElement | null>(
    null,
  );

  // 문제 보기 설정(JCG-2066): "안 푼 문제만"·"전체 보기 숨기기"를 우측 사이드바 필기 위에 둔다.
  // ChoicesToggleBar 가 컨트롤을 quizControlsSlot 에 portal. 컨트롤이 없으면 슬롯은 empty:hidden.
  const [quizControlsSlot, setQuizControlsSlot] =
    React.useState<HTMLElement | null>(null);
  // 종료 상태(모든 문제 풀이 완료 등 scoreActive=false)에서도 "안 푼 문제만" 토글을 유지하기 위한 신호.
  // 여러 컨트롤이 같은 슬롯을 공유하므로 호출자별 mount/unmount 를 카운트해 한쪽 cleanup 이
  // 다른 컨트롤까지 숨기지 않게 한다.
  const [controlsActive, setControlsActiveState] = React.useState(false);
  const controlsActiveCountRef = React.useRef(0);
  const setControlsActive = React.useCallback((active: boolean) => {
    controlsActiveCountRef.current = Math.max(
      0,
      controlsActiveCountRef.current + (active ? 1 : -1),
    );
    setControlsActiveState(controlsActiveCountRef.current > 0);
  }, []);

  // 사이드바 포털 슬롯 (JCG-2063 [D]) — PostLayout 내부의 목차/필기/출석이 createPortal 로
  // 자기 자신을 그릴 타깃. ref 콜백 setter 로 마운트 시 element, 언마운트 시 null 이 들어온다.
  const [tocSlot, setTocSlot] = React.useState<HTMLElement | null>(null);
  const [noteSlot, setNoteSlot] = React.useState<HTMLElement | null>(null);
  const [videoDockSlot, setVideoDockSlot] = React.useState<HTMLElement | null>(null);
  const [attendanceSlot, setAttendanceSlot] =
    React.useState<HTMLElement | null>(null);
  // 카테고리 인덱스 주요항목 슬롯(JCG-2098, 좌측 사이드바) — SubjectSidebarPortal 이 MajorTopics 를 portal.
  const [subjectSlot, setSubjectSlot] = React.useState<HTMLElement | null>(
    null,
  );

  // custom 셸 슬롯(JCG-2104 인덱스 셸 / JCG-2073 /my 대시보드) — variant="custom" 일 때만 좌/우
  // 컬럼에 렌더되며, 페이지(children) 안의 클라이언트 컴포넌트가 createPortal 로 자체 콘텐츠
  // (과목 네비·미리보기, /my 탭 네비·맥락 카드)를 그린다.
  const [customLeftSlot, setCustomLeftSlot] =
    React.useState<HTMLElement | null>(null);
  const [customRightSlot, setCustomRightSlot] =
    React.useState<HTMLElement | null>(null);

  // 좌/우 2열 콘텐츠 분기: ad-free(로그인 + starter↑) → 학습 도구(지도·목차·필기·출석),
  // 그 외(free·비로그인) → 광고. 단, 합격률 페이지 그래프처럼 공개 콘텐츠 위젯은 광고 분기에서도 노출한다.
  const { isAdFree, isLoaded, isSignedIn } = useEffectivePass();
  const showTools = isLoaded && isAdFree;
  const showLeftTools = showExamPreparation || showTools;

  // localStorage 복원 (마운트 후 — lazy useState init 으로 읽으면 서버[기본값]↔클라[저장값] 그리드
  // 인라인 스타일이 어긋나 hydration mismatch 가 나므로, 마운트 후 effect 에서 복원한다).
  // useRestoreOnMount 가 set-state-in-effect lint 처리 + deps 변화 시 재복원을 담당(JCG-2138).
  // variant 전환(글↔custom 셸 소프트 내비) 시 해당 variant 의 영속 폭을 다시 복원한다.
  // customRightDefault 는 custom↔custom(인덱스↔/my) 전환 시 기본 폭이 바뀌므로 함께 의존.
  useRestoreOnMount(() => {
    try {
      const l = Number(localStorage.getItem(LS_LEFT));
      // custom 셸은 article 필기 폭과 섞이지 않도록 별도 키에서 복원.
      const r = Number(
        localStorage.getItem(variant === "custom" ? LS_CUSTOM_RIGHT : LS_RIGHT),
      );
      const sw = Number(localStorage.getItem(LS_SOLUTION));
      const cw = Number(localStorage.getItem(LS_COMPOSE));
      const s = Number(localStorage.getItem(LS_LEFT_SPLIT));
      const sChecklist = Number(localStorage.getItem(LS_LEFT_SPLIT_CHECKLIST));
      const secMap = localStorage.getItem(LS_SEC_MAP);
      const secToc = localStorage.getItem(LS_SEC_TOC);
      const secScore = localStorage.getItem(LS_SEC_SCORE);
      const secProbNav = localStorage.getItem(LS_SEC_PROBNAV);
      const secPassRate = localStorage.getItem(LS_SEC_PASSRATE);
      const secChecklist = localStorage.getItem(LS_SEC_CHECKLIST);
      const splitPreference = restoreSolutionSplitPreference(
        isErrorNotesPath,
        isErrorNotesPath
          ? localStorage.getItem(LS_ERROR_NOTES_SOLUTION_SPLIT)
          : null,
        ERROR_NOTES_SPLIT_MIN,
        SPLIT_RATIO_MAX,
      );
      if (l) setLeftW(clamp(l, LEFT_MIN, LEFT_MAX));
      // 저장값이 없으면 variant 기본폭으로 복원 — 글↔custom 셸 소프트 내비 시 직전 variant 의
      // 폭이 잔류하지 않도록(custom=customRightDefault[/my 360·인덱스 420] / article=320) 무조건 설정.
      setRightW(
        r
          ? clamp(r, RIGHT_MIN, RIGHT_MAX)
          : variant === "custom"
            ? customRightDefault
            : RIGHT_DEFAULT,
      );
      if (sw) setSolutionW(clamp(sw, RIGHT_MIN, RIGHT_MAX));
      if (cw) setComposeW(clamp(cw, COMPOSE_MIN, COMPOSE_MAX));
      if (s) setMapPx(s);
      if (sChecklist) setChecklistPx(sChecklist);
      if (secMap !== null) setMapOpen(secMap === "true");
      if (secToc !== null) setTocOpen(secToc === "true");
      if (secScore !== null) setScoreOpen(secScore === "true");
      if (secProbNav !== null) setProblemNavOpen(secProbNav === "true");
      if (secPassRate !== null) setPassRateOpen(secPassRate === "true");
      if (secChecklist !== null) setChecklistOpen(secChecklist === "true");
      // 저장값이 없거나 손상된 오답노트에서는 ProblemAnswer가 현재 문제의 풀이 유무에 맞는 최초
      // 기본값을 정한다. 오답노트 밖에서는 helper가 0.5/미조절 상태를 반환해 전용 상태 누수를 막는다.
      errorNotesSplitAdjustedRef.current = splitPreference.userAdjusted;
      if (splitPreference.ratio !== null) setSplitRatio(splitPreference.ratio);
    } catch {
      errorNotesSplitAdjustedRef.current = false;
      // localStorage 자체가 비활성/차단된 경우에도 오답노트 전용 런타임 비율이 일반 풀이로
      // 새지 않도록, exact path 밖에서는 기본값 복구 계약을 지킨다.
      if (!isErrorNotesPath) setSplitRatio(0.5);
      /* ignore */
    }
  }, [variant, customRightDefault, isErrorNotesPath]);

  const onResizeLeft = React.useCallback((px: number) => {
    const v = clamp(px, LEFT_MIN, LEFT_MAX);
    setLeftW(v);
    writeLS(LS_LEFT, String(v));
  }, []);

  const onResizeRight = React.useCallback(
    (px: number) => {
      // 모드별 폭을 각자 클램프·영속: 작성(compose) > 풀이(solution) > 학습 도구/custom 셸(panels).
      // custom 셸은 article 필기 폭(LS_RIGHT)과 분리된 키(LS_CUSTOM_RIGHT)에 저장(아래 else).
      if (rightMode === "compose") {
        const v = clamp(px, COMPOSE_MIN, COMPOSE_MAX);
        setComposeW(v);
        writeLS(LS_COMPOSE, String(v));
      } else if (rightMode === "solution") {
        const v = clamp(px, RIGHT_MIN, RIGHT_MAX);
        setSolutionW(v);
        writeLS(LS_SOLUTION, String(v));
      } else {
        const v = clamp(px, baseRightMin, RIGHT_MAX);
        setRightW(v);
        writeLS(variant === "custom" ? LS_CUSTOM_RIGHT : LS_RIGHT, String(v));
      }
    },
    [rightMode, variant, baseRightMin],
  );

  // 풀이 모드 진입/종료(JCG-2065). 풀이 열기/닫기는 우측 패널 상태만 바꾸고,
  // 좌측 메뉴 접힘 상태는 사용자 토글 또는 컴팩트 구간 변화가 소유한다.
  // 이미 풀이 모드인 상태에서 다른 문제로 호출되면 activeSolutionId 교체 + 오답노트 리셋만 수행한다.
  const enterSolution = React.useCallback(
    (id: string) => {
      setActiveSolutionId(id);
      setRightMode("solution");
      setWrongOpen(false);
      setSolutionCollapsed(false);
    },
    [
      setActiveSolutionId,
      setRightMode,
      setWrongOpen,
      setSolutionCollapsed,
    ],
  );

  const exitSolution = React.useCallback(() => {
    setRightMode("panels");
    setWrongOpen(false);
    setActiveSolutionId(null);
    setSolutionCollapsed(false);
  }, [
    setRightMode,
    setWrongOpen,
    setActiveSolutionId,
    setSolutionCollapsed,
  ]);

  const expandSolution = () => setSolutionCollapsed(false);

  // 기본 설정이 없으면 컴팩트 구간 진입 시 자동 접힘, 명시한 기본값이 있으면 그 값을 우선한다.
  // 같은 구간에서 사용자가 누른 토글은 위 effect 의 의존값이 바뀔 때까지 유지한다.

  // 작성 모드 진입/종료(JCG-2102) — 복원 게시판 우측 사이드바를 PostEditor 로 변신.
  // 좌측(문제 네비)은 작성 중에도 유지하므로 leftCollapsed 는 건드리지 않는다.
  const enterCompose = React.useCallback(() => {
    // 진입 직전(아직 트리거 버튼이 포커스를 쥐고 있을 때) 포커스 복원 대상을 캡처한다.
    // 아래 isCompose effect 는 commit 후(=트리거가 hidden→blur 되어 activeElement 가 body 가 된 뒤)
    // 실행되므로 거기서 캡처하면 트리거가 아닌 body 가 저장된다(JCG-2123 #6 후속 수정).
    composeReturnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setRightMode("compose");
  }, []);
  const exitCompose = React.useCallback(() => setRightMode("panels"), []);

  // 풀이 ↕ 오답노트 분할 핸들 — clientY 를 분할 컨테이너 상대 비율로 변환. 클램프는 양쪽 pane 이
  // 붕괴되지 않게 하는 가드(기본 0.22~0.78). 오답노트 복습은 풀이가 작아도 되므로 풀이 pane 최소를
  // 0.1(오답노트 최대 90%)로 완화한다(일반/forum 은 0.22 유지).
  const onResizeSolutionSplit = React.useCallback(
    (clientY: number) => {
      const el = solutionSplitRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const ratio = (clientY - r.top) / r.height;
      const minRatio = isErrorNotesPath ? ERROR_NOTES_SPLIT_MIN : SPLIT_RATIO_MIN;
      // 분수 비율은 반올림 없는 clampRatio 사용. 공유 clamp(Math.round 포함)를 쓰면 0/1 양자화로
      // splitRatio 가 minRatio↔0.78 토글로 붕괴해 핸들을 한 방향으로만 움직이게 된다 (JCG-2156).
      const next = clampRatio(ratio, minRatio, SPLIT_RATIO_MAX);
      setSplitRatio(next);
      if (isErrorNotesPath) {
        errorNotesSplitAdjustedRef.current = true;
        writeLS(LS_ERROR_NOTES_SOLUTION_SPLIT, next);
      }
    },
    [isErrorNotesPath],
  );

  // ProblemAnswer가 활성 문제의 풀이 유무에 맞춰 요청하는 최초 분할값 어댑터. 오답노트에서
  // 사용자가 한 번 조절(또는 복원)한 뒤에는 문제 이동 effect가 그 값을 다시 0.5/0.1로 덮지 않는다.
  // 다른 article 경로는 기존 setSplitRatio 동작을 그대로 통과시킨다.
  const setProblemSplitRatio = React.useCallback(
    (ratio: number) => {
      const next = resolveProblemSplitRatioUpdate(
        ratio,
        isErrorNotesPath,
        errorNotesSplitAdjustedRef.current,
      );
      if (next !== null) setSplitRatio(next);
    },
    [isErrorNotesPath],
  );

  // 접이식 Section 토글(영속) — 지도/점수/문제목록/목차 공용 헬퍼. 목차는 셸이 펼침 상태를
  // 소유(TableOfContents 는 리스트만 portal), 지도는 스플릿 높이를 보존한다.
  const toggleMap = usePersistedToggle(setMapOpen, LS_SEC_MAP);
  const toggleScore = usePersistedToggle(setScoreOpen, LS_SEC_SCORE);
  const toggleProblemNav = usePersistedToggle(
    setProblemNavOpen,
    LS_SEC_PROBNAV,
  );
  const toggleToc = usePersistedToggle(setTocOpen, LS_SEC_TOC);
  const togglePassRate = usePersistedToggle(setPassRateOpen, LS_SEC_PASSRATE);
  const toggleChecklist = usePersistedToggle(
    setChecklistOpen,
    LS_SEC_CHECKLIST,
  );

  // 세로 스플릿 핸들 드래그: clientY → 스플릿 컨테이너 상대 높이로 변환해 지도 pane 높이 설정.
  // ResizeHandle orientation="h" 는 onResize 에 clientY 를 준다. 각 pane 은 최소 SPLIT_MIN 보장.
  const onResizeSplit = React.useCallback((clientY: number) => {
    const el = splitRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const h = el.clientHeight;
    const next = clamp(
      clientY - top,
      SPLIT_MIN,
      Math.max(SPLIT_MIN, h - SPLIT_MIN),
    );
    setMapPx(next);
    writeLS(LS_LEFT_SPLIT, String(next));
  }, []);

  // 체크리스트 ↕ 목차 세로 스플릿 핸들 드래그(JCG-2101) — 지도 스플릿과 동일 메커니즘, 체크리스트
  // pane 높이(checklistPx)를 설정·영속. 컨테이너는 checklistSplitRef.
  const onResizeChecklistSplit = React.useCallback((clientY: number) => {
    const el = checklistSplitRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const h = el.clientHeight;
    const next = clamp(
      clientY - top,
      SPLIT_MIN,
      Math.max(SPLIT_MIN, h - SPLIT_MIN),
    );
    setChecklistPx(next);
    writeLS(LS_LEFT_SPLIT_CHECKLIST, String(next));
  }, []);

  // 접힘 시 1열(아이콘 레일, 58px)은 유지하고 2열(콘텐츠 슬롯)만 숨긴다. 풀이 모드에서도
  // 메뉴 토글로 좌측 지도·목차에 접근 가능하며, 풀이 열기/닫기는 좌측 상태를 바꾸지 않는다.
  // 작성 모드(compose)는 복원 게시판 전용 — rightMode 는 셸이 소유해 페이지 이동에도 유지되므로,
  // restoration 을 벗어나면(isRestoration=false) compose 가 다른 셸 페이지로 누수돼 우측 폭이
  // 720px 로 벌어진 채 일반 패널이 렌더되는 것을 막기 위해 panels 로 리셋한다(JCG-2102).
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!isRestoration && rightMode === "compose") setRightMode("panels");
  }, [isRestoration, rightMode]);

  // 풀이 모드 누수 방어(JCG-2126): /my 오답노트 탭에서 풀이를 연 채로 다른 /my 탭으로 소프트
  // 내비하면 ProblemAnswer 가 언마운트돼 exitSolution 을 호출하지 않으므로 rightMode 가 'solution'
  // 으로 잔류해 빈 풀이 패널이 누수된다. 오답노트(유일하게 풀이를 여는 /my 탭)를
  // 벗어나면 학습 도구/맥락 모드로 리셋한다. (article 셸은 ProblemAnswer 의 activeSolutionId
  //  가드로 처리되므로 건드리지 않음 — custom 셸 전용 방어.) isErrorNotesPath 는 위에서 hoist.
  React.useEffect(() => {
    if (variant === "custom" && !isErrorNotesPath && rightMode === "solution") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      exitSolution();
    }
  }, [variant, isErrorNotesPath, rightMode, exitSolution]);

  // compose 는 복원 페이지에서만 유효 — 위 effect 가 리셋하기 전 1프레임 누수도 막도록 가드.
  // (풀이 모드 여부는 rightColumn.kind==="solution" 로 판별 — JCG-2141. isCompose 는 a11y 포커스
  //  effect 의 의존성으로 남겨둔다.)
  const isCompose = rightMode === "compose" && isRestoration;

  // custom 셸에서 우측 "맥락" 컬럼을 숨기는지(/my 대시보드 홈 + 오답노트, JCG-2073/2060).
  // 우측 컬럼 union(deriveRightColumn) 입력과 custom 렌더 분기에서 공유한다. 오답노트는 평소 2컬럼이되,
  // 풀이/오답노트를 열면 rightMode='solution' 이 hideRight 보다 우선해 우측 패널이 슬라이드 열린다.
  const hideRight = isMyTwoColumnPage(pathname);

  // 작성 모드 a11y(JCG-2123 #6): 진입 시 패널로 포커스 이동(트리거 버튼이 hidden 이라 포커스 고립 방지),
  // 닫을 때 진입 전 트리거 요소로 포커스 복원(overlay 포커스 복원 규칙). 닫기(exitCompose)는
  // onKeyDown(Escape)·닫기 버튼이 담당. 풀이 모드(SolutionAside)는 공유 컴포넌트 파리티 보존을 위해
  // 건드리지 않는다(별도 처리 대상).
  React.useEffect(() => {
    if (!isCompose) return;
    // 복원 대상은 enterCompose 가 진입 직전에 캡처한다(여기서 잡으면 트리거가 이미 hidden→blur 됨).
    // 이 effect 는 진입 시 패널로 포커스만 옮긴다(트리거가 hidden 이라 포커스 고립 방지).
    composePanelRef.current?.focus();
    return () => {
      // 닫힘(또는 restoration 이탈) 시 진입 전 트리거가 아직 문서에 살아 있으면 포커스 복원.
      const target = composeReturnFocusRef.current;
      composeReturnFocusRef.current = null;
      if (target && target.isConnected) target.focus();
    };
  }, [isCompose]);

  // 우측 컬럼 discriminated union (JCG-2141) — rightMode/불리언 조합 + rightWidthCss 삼항을
  // deriveRightColumn 단일 소스로 환원. kind 별 폭(widthCss)/배경(gray)/리사이즈(showResize)를
  // 들고 와 아래 우측 콘텐츠 분기 + ShellFrame prop(rightWidthCss/showRightResize/배경)에 주입한다.
  // 우선순위: compose > solution > custom > panels > none.
  // - custom 셸은 풀이/작성 모드로 변신하지 않으므로(우측은 항상 맥락 슬롯) panels 고정으로 도출
  //   (rightMode 가 잔류해도 우측 폭은 ${rightW}px, kind 는 custom/none).
  // - solution 폭은 "숨기기"(레일) 여부를 미리 반영해 전달(SOLUTION_RAIL ↔ solutionW).
  // 컴팩트 셸(JCG-2224) 우측 폭 캡 — 레일(58) + 우측 420 기준 1024px에서 본문 ~546px 확보.
  // 영속값(localStorage)은 건드리지 않고 렌더 시 유효 폭만 캡한다(shell-primitives 공용 헬퍼).
  const capRight = (px: number) => capCompactWidth(px, compact);
  const rightWidths = {
    compose: `${capRight(composeW)}px`,
    solution: solutionCollapsed
      ? `${SOLUTION_RAIL}px`
      : `${capRight(solutionW)}px`,
    // 저장된 좁은 폭에서도 모바일 2열 결제 위젯을 위한 최소 폭을 확보한다.
    // 다른 custom 페이지의 저장값은 바꾸지 않고 PDF에서만 유효 폭을 보정한다.
    base: `${Math.max(capRight(rightW), baseRightMin)}px`,
  };
  // custom 셸도 풀이 모드(rightMode==='solution')는 존중한다(JCG-2126). /my 오답노트 탭은
  // custom 셸 안에서 ProblemAnswer(useShellSolution)가 enterSolution 을 호출하므로, 우측을
  // 맥락 슬롯(custom) → 풀이(solution)로 변신시켜야 한다. compose 는 custom 셸과 무관(복원
  // 게시판 전용)이므로 panels 로 강등해 누수를 막는다.
  const customRightMode = rightMode === "solution" ? "solution" : "panels";
  const rightColumn =
    variant === "custom"
      ? deriveRightColumn(
          { inShell: true },
          { rightMode: customRightMode },
          {
            isCustom: true,
            hideRight,
            isRestoration,
            solutionCollapsed,
            widths: rightWidths,
          },
        )
      : deriveRightColumn(
          { inShell: true },
          { rightMode },
          {
            isCustom: false,
            hideRight,
            isRestoration,
            solutionCollapsed,
            widths: rightWidths,
          },
        );
  // 풀이/작성은 독립적인 패널이므로 열 때 본래 크기를 사용한다. 종료하면 이전 접힘 상태로 돌아온다.
  // 광고가 표시되는 Free/비로그인 사용자는 오른쪽 컬럼을 접을 수 없다.
  const panelsCollapsed =
    showTools &&
    rightCollapsed &&
    (rightColumn.kind === "panels" || rightColumn.kind === "custom");
  const rightSidebarLabel = variant === "article" ? "도구" : "패널";
  const rightRail = panelsCollapsed && (
    <div className="flex h-full flex-col items-center gap-3 py-3">
      <button
        type="button"
        onClick={() => setRightDefault(false)}
        title={`${rightSidebarLabel} 펼치기`}
        aria-label={`${rightSidebarLabel} 펼치기`}
        aria-expanded={false}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-amber-600 transition-colors hover:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-300 dark:hover:border-amber-700"
      >
        <PanelRightOpen className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>
      <span className="text-[11px] font-semibold text-muted-foreground [writing-mode:vertical-rl]">
        {rightSidebarLabel}
      </span>
    </div>
  );

  // 영역(left/right/header) 기반 슬롯 레지스트리 (JCG-2140). 우측 변신 모드(풀이/작성)는
  // solutionMode 로 분리되며, 여기엔 모드 무관한 정적 슬롯 + cross-cutting 신호만 담는다.
  const slotRegistry = React.useMemo<SlotRegistry>(
    () => ({
      inShell: true,
      left: {
        tocSlot,
        scoreSlot,
        problemNavSlot,
        subjectSlot,
        customLeftSlot,
        restorationLeftSlot,
      },
      right: {
        noteSlot,
        attendanceSlot,
        quizControlsSlot,
        customRightSlot,
        restorationRightSlot,
      },
      header: { shellHeaderSlot, headerActionSlot },
      attendance: { attendanceVersion, refreshAttendance },
      sections: { setScoreActive, setControlsActive },
    }),
    [
      tocSlot,
      scoreSlot,
      problemNavSlot,
      subjectSlot,
      customLeftSlot,
      restorationLeftSlot,
      noteSlot,
      attendanceSlot,
      quizControlsSlot,
      customRightSlot,
      restorationRightSlot,
      shellHeaderSlot,
      headerActionSlot,
      attendanceVersion,
      refreshAttendance,
      setScoreActive,
      setControlsActive,
    ],
  );

  // 우측 컬럼 변신 모드(풀이/작성) 상태 + 액션 (JCG-2140). enter/exit/setWrongOpen 어댑터
  // 경유 호환 유지(필수 보존). solutionSlot/errorNoteSlot/composeSlot 은 모드 전용 슬롯.
  const solutionMode = React.useMemo<SolutionMode>(
    () => ({
      rightMode,
      solutionSlot,
      errorNoteSlot,
      activeSolutionId,
      wrongOpen,
      solutionCollapsed,
      enterSolution,
      exitSolution,
      setWrongOpen,
      setSplitRatio: setProblemSplitRatio,
      composeSlot,
      enterCompose,
      exitCompose,
    }),
    [
      rightMode,
      solutionSlot,
      errorNoteSlot,
      activeSolutionId,
      wrongOpen,
      solutionCollapsed,
      enterSolution,
      exitSolution,
      setWrongOpen,
      setProblemSplitRatio,
      composeSlot,
      enterCompose,
      exitCompose,
    ],
  );

  // 풀이 모드 chrome — SolutionAside 공유 컴포넌트(JCG-2105 추출). 셸이 헤더·50/50 분할·
  // 드래그 핸들을 소유하고, ProblemAnswer 가 풀이 본문/오답노트를 solutionSlot/errorNoteSlot 에
  // portal. article 경로(아래)와 custom 셸 풀이 모드(JCG-2126, 위 early-return)가 공유하므로,
  // 두 분기 모두에서 참조할 수 있도록 custom 분기 이전에 정의한다.
  const solutionAside = (
    <SolutionAside
      activeSolutionId={activeSolutionId}
      solutionCollapsed={solutionCollapsed}
      onHide={() => setSolutionCollapsed(true)}
      onExpand={expandSolution}
      onExit={exitSolution}
      wrongOpen={wrongOpen}
      setWrongOpen={setWrongOpen}
      splitRatio={splitRatio}
      solutionSplitRef={solutionSplitRef}
      onResizeSolutionSplit={onResizeSolutionSplit}
      setSolutionSlot={setSolutionSlot}
      setErrorNoteSlot={setErrorNoteSlot}
    />
  );

  // 목차 Section — 일반 글(지도+목차)과 비이론 글(목차만) 좌측 분기에서 공용. tocSlot 은 한 번에
  // 한 분기만 렌더되므로 동일 element 재사용 안전(TableOfContents 가 tocSlot 에 리스트 portal).
  const tocSection = (
    <Section
      icon={List}
      label="목차"
      open={tocOpen}
      onToggle={toggleToc}
      fill={tocOpen}
    >
      <div ref={setTocSlot} className="min-h-0 flex-1 overflow-y-auto" />
    </Section>
  );

  /*
    custom 셸(variant="custom") — 별도 단순 렌더 경로.
    인덱스 셸(/forum/jcg, JCG-2104) · /my 통합 대시보드(JCG-2073)가 공유한다.
    좌/우 사이드바를 페이지(children)가 custom 슬롯에 직접 주입한다(지도·점수·풀이·광고 로직 없음).
    공유 chrome(아이콘 레일·슬림 헤더·드래그 리사이즈·폭 영속)만 article 경로와 재사용.
    가운데 본문은 인덱스 셸은 max-w 캡(820px), /my 대시보드는 캡 없이 컬럼 폭을 채운다
    (대시보드/오답노트 통계가 넓게 보이도록). 좌측은 leftCollapsed 시 레일(58px)만.
  */
  if (variant === "custom") {
    // /my 인덱스(대시보드 홈)는 우측 맥락 사이드바 없이 2컬럼으로 본문 폭을 최대화한다(JCG-2073).
    // 각 탭·인덱스 셸(/forum/jcg)은 3컬럼 + 우측 맥락 카드/미리보기 유지.
    // rightColumn.kind 가 "none"(=hideRight) 이면 2컬럼, 그 외(custom/solution)면 3컬럼.
    // 풀이 모드(kind="solution", JCG-2126): /my 오답노트 탭에서 ProblemAnswer 가 enterSolution 을
    // 호출하면 우측이 맥락 슬롯 → 풀이로 변신한다. customRightSlot div 는 hidden 으로 mount 유지해
    // 탭/필터 카드 portal·ref 를 보존하고(언마운트 시 회귀), 풀이 chrome(SolutionAside)을 덧그린다.
    const customSolution = rightColumn.kind === "solution";
    return (
      <SlotRegistryContext.Provider value={slotRegistry}>
       <SolutionModeContext.Provider value={solutionMode}>
        <ShellFrame
          // 우측 맥락 카드 유무로 3컬럼/2컬럼 분기. 좌측은 레일+콘텐츠 슬롯.
          columns={rightColumn.kind === "none" ? "left+main" : "left+main+right"}
          // 좌측 폭: 접힘 시 레일(58px) override, 그 외 leftW. resize 는 ArticleShell 소유(LS_LEFT).
          // 풀이 모드 열기/닫기는 좌측 메뉴 상태를 건드리지 않는다.
          leftWidthCss={leftCollapsed ? `${RAIL_WIDTH}px` : `${leftW}px`}
          onResizeLeft={onResizeLeft}
          showLeftContent={!leftCollapsed}
          // 우측 폭: custom 셸 키(LS_CUSTOM_RIGHT)는 onResizeRight 가 처리, 폭은 rightW.
          // kind 가 "none"(2컬럼)이면 우측 미렌더라 widthCss 가 무시되므로 base 폭으로 폴백
          // (컴팩트 셸에선 capRight 로 캡, JCG-2224). kind 가 "solution"이면 풀이 폭(solutionW / 레일)으로 변신.
          rightWidthCss={
            panelsCollapsed
              ? `${SOLUTION_RAIL}px`
              : rightColumn.kind === "none"
                ? `${capRight(rightW)}px`
                : rightColumn.widthCss
          }
          onResizeRight={onResizeRight}
          // 풀이 모드 우측 배경은 회색(article 풀이와 동일), 숨기기 레일이면 리사이즈 핸들 숨김.
          rightAsideClassName={
            customSolution ? "bg-gray-50 dark:bg-gray-900/40" : "bg-background"
          }
          showRightResize={
            !panelsCollapsed && (customSolution ? rightColumn.showResize : true)
          }
          header={{
            leftCollapsed,
            onToggleSidebar: () => setLeftCollapsed((v) => !v),
            rightCollapsed,
            rightSidebarLabel,
            onToggleRightSidebar:
              showTools && rightColumn.kind === "custom"
                ? () => setRightDefault(!rightCollapsed)
                : undefined,
            categoryLabel,
            // custom 셸은 상단바에 페이지/탭 제목을 상시 표시.
            staticTitle: isHomeDashboard
              ? HOME_SHELL_LABEL
              : isMyDashboard
                ? getMyDashboardLabel(pathname)
                : isPdfDetail
                  ? SUMMARY_PDF_DETAIL_LABEL
                  : isIssueBoard
                    ? COMMUNITY_ISSUES_SHELL_LABEL
                    : isTalkBoard
                      ? COMMUNITY_TALK_SHELL_LABEL
                      : getIndexShellLabel(pathname),
            // 검색창 왼쪽 액션 슬롯 배선 (JCG-3283). /my 대시보드의 "지난 회차 보기" 버튼이
            // 이 슬롯에 portal 한다. 아무도 portal 하지 않는 다른 custom 셸에서는 슬롯 div가
            // empty:hidden 으로 사라져 시각 변화가 없다.
            headerActionSlotRef: setHeaderActionSlot,
          }}
          // 좌측 사이드바 (페이지 주입 슬롯, 접힘 시 미렌더)
          left={
            <div
              ref={setCustomLeftSlot}
              className="flex min-w-0 flex-1 flex-col overflow-y-auto p-3"
            />
          }
          // 우측 사이드바: 탭별 맥락 카드·미리보기 슬롯(customRightSlot)은 항상 mount(풀이 모드 땐
          // hidden 으로 보존 → 탭/필터 portal·ref 유지). 풀이 모드면 SolutionAside 를 덧그린다.
          right={
            <>
              <div
                ref={setCustomRightSlot}
                className={cn(
                  "flex min-h-0 flex-1 flex-col overflow-y-auto p-3",
                  (customSolution || panelsCollapsed) && "hidden",
                )}
              />
              {rightRail}
              {customSolution && solutionAside}
            </>
          }
        >
          <div
            className={cn(
              "mx-auto w-full px-4 pb-20 sm:px-8",
              // 인덱스 셸(/forum/jcg)은 본문(ID 그리드)을 820px 로 캡. /my 대시보드는 통계가 넓게
              // 보이도록 캡 없이 컬럼 폭을 채운다. 홈 대시보드는 히어로·과목 그리드가 과하게 늘어나지
              // 않도록 880px 캡(내부 컴포넌트는 자체 max-w 로 중앙 정렬). 요약 PDF 상세(JCG-2121)는
              // 캐러셀·세일즈 본문이 넉넉히 보이도록 900px 캡(구매 CTA 는 우측 sticky 슬롯).
              isMyDashboard
                ? "pt-4"
                : isHomeDashboard
                  ? "max-w-[880px] pt-4"
                  : isPdfDetail
                    ? "max-w-[900px] pt-4"
                    : "max-w-[820px] pt-2",
            )}
          >
            {children}
            {footer}
          </div>
        </ShellFrame>

        {globals}
       </SolutionModeContext.Provider>
      </SlotRegistryContext.Provider>
    );
  }

  // 좌측 사이드바 분기 트리(레일/접힘/리사이즈 핸들은 ShellFrame 소유).
  const leftTree = (
    <>
      {isRestoration ? (
        // 복원 게시판(JCG-2102): 지도·목차·점수 대신 진행바 + 문제 네비를 페이지가 주입.
        // 로그인/등급 무관 동일 노출(포럼형). RestorationPageClient 가 슬롯에 portal.
        <div
          ref={setRestorationLeftSlot}
          className="flex min-w-0 flex-1 flex-col overflow-y-auto p-3"
        />
      ) : isSubjectIndex ? (
        // 카테고리 인덱스(JCG-2098): 좌측은 주요항목(MajorTopics). 페이지가 SubjectSidebarPortal
        // 로 MajorTopics 를 이 슬롯에 portal 한다. 도구/광고 분기 없이 모든 사용자에게 노출
        // (과목 페이지는 사이드바 광고 미노출 정책). 목차·필기·출석·점수는 인덱스에 없다.
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-3">
          <div
            ref={setSubjectSlot}
            className="min-h-0 flex-1 overflow-y-auto"
          />
        </div>
      ) : showLeftTools ? (
        scoreActive && !showExamPreparation ? (
          // 기출·퀴즈 페이지(JCG-2066): 지도/목차는 의미가 없으므로 점수 + 문제 목록으로 대체.
          // 풀이 모드(우측 변신, JCG-2065)에서도 좌측을 다시 열면 정답률·문제 이동이 계속 가능.
          // ArticleScorePanel → scoreSlot, ProblemListNav → problemNavSlot 에 portal.
          <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden p-3">
            <Section
              icon={Trophy}
              label="점수"
              amber
              open={scoreOpen}
              onToggle={toggleScore}
            >
              <div ref={setScoreSlot} />
            </Section>
            <Section
              icon={ListOrdered}
              label="문제 목록"
              open={problemNavOpen}
              onToggle={toggleProblemNav}
              fill={problemNavOpen}
            >
              <div
                ref={setProblemNavSlot}
                className="min-h-0 flex-1 overflow-y-auto"
              />
            </Section>
          </div>
        ) : showApplication ? (
          // 원서 접수 페이지(JCG-2101): 체크리스트 ↕ 목차 세로 스플릿(지도↕목차와 동일 메커니즘).
          // 가운데 가로 핸들로 두 pane 높이를 나눈다(둘 다 펼침일 때만 핸들 + checklistPx 고정).
          <div
            ref={checklistSplitRef}
            className="flex min-w-0 flex-1 flex-col overflow-hidden p-3"
          >
            {/* 체크리스트 pane */}
            <div
              className={cn(
                "flex min-h-0 flex-col",
                !checklistOpen
                  ? "flex-none"
                  : checklistOpen && !tocOpen
                    ? "flex-1"
                    : "",
              )}
              style={
                checklistOpen && tocOpen ? { height: checklistPx } : undefined
              }
            >
              <Section
                icon={ListChecks}
                label="원서 접수 체크리스트"
                amber
                fill={checklistOpen}
                open={checklistOpen}
                onToggle={toggleChecklist}
              >
                <ChecklistMini />
              </Section>
            </div>

            {/* 분할 핸들 — 둘 다 펼침일 때만 */}
            {checklistOpen && tocOpen && (
              <ResizeHandle orientation="h" onResize={onResizeChecklistSplit} />
            )}

            {/* 목차 pane */}
            <div
              className={cn(
                "flex min-h-0 flex-col",
                tocOpen ? "flex-1" : "flex-none",
                !(checklistOpen && tocOpen) && "mt-2",
              )}
            >
              {tocSection}
            </div>
          </div>
        ) : !isLearning ? (
          // 이론 학습이 아닌 글(기출 해설·학습 계획·예상문제 등): 지도 없이 목차만(JCG-2066).
          // 합격률 페이지(JCG-2101)는 목차 위에 실기 합격률 추이 미니 차트를 추가로 노출.
          <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden p-3">
            {showPassRate && (
              <Section
                icon={Activity}
                label="실기 합격률 추이"
                amber
                open={passRateOpen}
                onToggle={togglePassRate}
                className="shrink-0"
              >
                <PassRateMiniChart />
              </Section>
            )}
            {tocSection}
          </div>
        ) : (
          // ad-free 사용자 전용 좌측 도구 — 지도 ↕ 목차 세로 스플릿(JCG-2063).
          // 가운데 가로 핸들로 두 pane 높이를 나눈다(지도를 늘리면 목차가 줄고 그 반대도).
          // 핸들은 둘 다 펼침일 때만 보이고, 그때만 지도 pane 이 고정 높이(mapPx)를 갖는다.
          // 목차 Section 본문(tocSlot)에는 TableOfContents 가 리스트만 portal 한다.
          <div
            ref={splitRef}
            className="flex min-w-0 flex-1 flex-col overflow-hidden p-3"
          >
            {/* 지도 pane */}
            <div
              className={cn(
                "flex min-h-0 flex-col",
                !mapOpen ? "flex-none" : mapOpen && !tocOpen ? "flex-1" : "",
              )}
              style={mapOpen && tocOpen ? { height: mapPx } : undefined}
            >
              <Section
                icon={MapIcon}
                label={<MapSectionLabel />}
                amber
                fill={mapOpen}
                open={mapOpen}
                onToggle={toggleMap}
              >
                <TrailMap />
              </Section>
            </div>

            {/* 분할 핸들 — 둘 다 펼침일 때만. 한쪽이라도 접히면 핸들 없이 헤더만 쌓인다. */}
            {mapOpen && tocOpen && (
              <ResizeHandle orientation="h" onResize={onResizeSplit} />
            )}

            {/* 목차 pane — TableOfContents 가 tocSlot 에 <ul> 만 portal */}
            <div
              className={cn(
                "flex min-h-0 flex-col",
                tocOpen ? "flex-1" : "flex-none",
                // 핸들이 없을 때(목차 접힘 등) 두 헤더가 붙지 않도록 간격
                !(mapOpen && tocOpen) && "mt-2",
              )}
            >
              {tocSection}
            </div>
          </div>
        )
      ) : (
        // free·비로그인 — 공개 콘텐츠 위젯(합격률 그래프) + 사이드바 광고(세로 중앙).
        // 미로그인 시 leftAd 가 스스로 null.
        // 기출·퀴즈 페이지면 광고 위에 점수/문제 목록 Section 을 얹어 로그인 불문 정답률·문제 이동 노출(JCG-2066).
        <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-hidden p-3">
          {showPassRate && (
            <Section
              icon={Activity}
              label="실기 합격률 추이"
              amber
              open={passRateOpen}
              onToggle={togglePassRate}
              className="shrink-0"
            >
              <PassRateMiniChart />
            </Section>
          )}
          {scoreActive && (
            <Section
              icon={Trophy}
              label="점수"
              amber
              open={scoreOpen}
              onToggle={toggleScore}
              className="shrink-0"
            >
              <div ref={setScoreSlot} />
            </Section>
          )}
          {scoreActive && (
            <Section
              icon={ListOrdered}
              label="문제 목록"
              open={problemNavOpen}
              onToggle={toggleProblemNav}
              fill={problemNavOpen}
            >
              <div
                ref={setProblemNavSlot}
                className="min-h-0 flex-1 overflow-y-auto"
              />
            </Section>
          )}
          {/* 지도(TrailMap) — 로그인 Free 에게 이론 학습 글에서 고정 높이로 노출(JCG-2811 확장, 데스크탑 Free 개방).
              starter+ 와 달리 목차·세로 리사이즈 핸들은 없다: 펼치면 MAP_DEFAULT 고정 높이(내부 스크롤),
              접으면 헤더만. 광고(leftAd)는 아래에 그대로 유지 — Free 는 광고 노출을 유지한다. */}
          {isSignedIn && !scoreActive && isLearning && (
            // 펼침이면 MAP_DEFAULT 고정 높이(style), 접힘이면 헤더만(자연 높이). 두 경우 모두
            // 광고에 자리를 양보하지 않도록 `shrink-0` — 높이 차이는 style 이 담당(리사이즈 없음).
            <div
              className="flex min-h-0 shrink-0 flex-col"
              style={mapOpen ? { height: MAP_DEFAULT } : undefined}
            >
              <Section
                icon={MapIcon}
                label={<MapSectionLabel />}
                amber
                fill={mapOpen}
                open={mapOpen}
                onToggle={toggleMap}
              >
                <TrailMap />
              </Section>
            </div>
          )}
          {/* 반응형 세로 광고(JCG-2194): 남은 세로 공간에 세로 중앙 정렬.
              AdSenseSidebar 가 자체 300px 박스(mx-auto)를 반환하므로 justify-center 가 직접 중앙 정렬. */}
          <div className="flex min-h-[160px] flex-1 items-center justify-center overflow-hidden">
            {leftAd}
          </div>
        </div>
      )}
    </>
  );

  // 시험장 찾기 배너는 원서 사전입력 페이지(jeongcheogi-pre-application)에서만 좌측 최상단에
  // 노출(JCG-2311). 그 외 페이지는 기존과 동일하게 분기 트리만 렌더(래퍼 없음).
  // 외곽을 세로 flex 컬럼으로 감싸 배너는 shrink-0(자연 높이), 아래 분기 트리는 flex-1 로
  // 남은 높이를 차지·내부 스크롤을 그대로 유지한다.
  const baseLeftContent = isPreApplicationPage(pathname) ? (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 px-3 pb-0 pt-3">
        <TestCenterBanner />
      </div>
      {leftTree}
    </div>
  ) : (
    leftTree
  );

  const leftContent = isMockExamShell ? (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col",
        mockExamSidebarSurface,
      )}
    >
      {baseLeftContent}
    </div>
  ) : (
    baseLeftContent
  );

  /* 우측 사이드바 콘텐츠 (col 3, DOM 2번째):
      ad-free(starter↑) → 필기·출석 도구 스택(JCG-2063 [D]) / free·비로그인 → 광고.
      풀이 모드(JCG-2065)면 학습 도구를 hidden 으로 숨기고(슬롯 ref 보존) 풀이 chrome 표시.
      배경색·리사이즈 핸들 노출은 rightColumn(union, JCG-2141)에서 파생해 ShellFrame 에 위임.

      ── 우측 콘텐츠 블록(아래 4개)은 모드 전환 시 비활성 블록을 hidden 으로 mount 유지해
         slot ref(noteSlot/restorationRightSlot 등)와 미저장 입력·스크롤을 보존한다. 블록은
         고정 순서 프래그먼트로 mount 하고(상호 배타 switch 아님 — 순서가 바뀌면 ref remount),
         hidden/mount 여부만 rightColumn.kind(solution/compose)로 가른다. panels 내부의
         학습 도구 ↔ 복원 도구 분기만 isRestoration 으로 가른다. */

  // 학습 도구(panels) — 복원 게시판은 학습 도구가 없으므로 미렌더(!isRestoration).
  // noteSlot/attendanceSlot ref 를 살려두어 Note 의 미저장 텍스트/스크롤 위치를 보존한다.
  // 풀이 모드(kind="solution")일 때 hidden 으로 보존.
  const learningPanels = !isRestoration && (
        <div
          data-article-learning-panels
          className={cn(
            "flex flex-1 flex-col gap-3 overflow-hidden p-3",
            videoDockStyles.panels,
            (rightColumn.kind === "solution" || panelsCollapsed) && "hidden",
          )}
        >
          <div
            ref={setVideoDockSlot}
            data-article-video-dock
            className="shrink-0 empty:hidden"
          />
          <div className={cn(
            "flex min-h-0 flex-1 flex-col gap-3 overflow-hidden",
            videoDockStyles.tools,
            showExamPreparation && "overflow-y-auto overscroll-contain",
          )}>
          {/* 점수·문제 목록은 좌측 사이드바(지도/목차 자리)로 이동(JCG-2066) — 풀이 모드(우측 변신)에서도
                  좌측을 열면 정답률·문제 이동이 가능. 우측은 필기 + (이론 학습 글 한정)출석 / 광고.
                  출석은 이론 학습 글에서만 — 문제 풀이(scoreActive)·기출 해설·학습계획·예상문제엔 미노출
                  (attendanceSlot 미렌더 → AttendanceButton 이 셸에서 null 반환, 플로팅 폴백 없음). */}
          {showExamPreparation ? (
            <>
              <ExamPreparationCard pathname={pathname} />
              {/* 합격률 페이지의 기존 회차별 후기 링크는 공개 안내와 함께 유지한다. */}
              {showPassRate && <PassRateReviewCard />}
            </>
          ) : isSubjectIndex ? (
            // 카테고리 인덱스(JCG-2098): 우측은 카테고리 지도(TrailMap). "현재 정거장"이 없으므로
            // 전체 정거장/진행도만 표시(TrailMap 이 출석 기준 진행도 계산). 도구/광고 분기 없이 모든
            // 사용자에게 노출(과목 페이지는 사이드바 광고 미노출 정책). 필기·출석·점수는 인덱스에 없다.
            <Section
              icon={MapIcon}
              label={<MapSectionLabel />}
              amber
              fill={mapOpen}
              open={mapOpen}
              onToggle={toggleMap}
            >
              <TrailMap />
            </Section>
          ) : showTools ? (
            <>
              {/* 문제 보기 설정(안 푼 문제만·전체 보기 숨기기) — 퀴즈·기출에서 필기 위에.
                      ChoicesToggleBar 가 컨트롤을 portal. 컨트롤 없으면 empty:hidden 으로 사라짐. */}
              {(scoreActive || controlsActive) && (
                <div
                  ref={setQuizControlsSlot}
                  className="shrink-0 space-y-2 empty:hidden"
                />
              )}
              <div
                ref={setNoteSlot}
                className={cn("flex min-h-0 flex-1 flex-col", videoDockStyles.note)}
              />
              {/* 출석은 이론 학습 글에서만 — 기출·모의고사·해설·학습계획·예상문제엔 미노출(JCG-2066) */}
              {!scoreActive && isLearning && (
                <div ref={setAttendanceSlot} className="shrink-0" />
              )}
            </>
          ) : (
            // free·비로그인: 광고 분기. 단 출석체크는 Free 로그인 사용자에게도 개방(JCG-2811)하므로
            // 이론 학습 글(!scoreActive && isLearning)에서는 광고 아래(우측 사이드바 하단)에 출석 슬롯을 렌더한다.
            // 지도·목차·필기 도구는 starter+(showTools) 전용을 유지 — 출석만 Free 에 연다.
            <>
              {/* 반응형 세로 광고(JCG-2194): 출석(하단 고정)을 제외한 남은 세로 공간에 세로 중앙 정렬.
                  AdSenseSidebar 가 자체 300px 박스(mx-auto)를 반환하므로 flex-1 로 남은 높이를 차지하고
                  justify-center 가 박스를 직접 중앙 정렬한다. */}
              {/* The video spacer above tools reserves its own area before ads. */}
              <div
                data-article-sidebar-ad
                className="flex flex-1 items-center justify-center"
              >
                {rightAd}
              </div>
              {/* 출석 슬롯 — 우측 사이드바 하단(광고 아래)에 고정. 좌측 지도(상단)와 대각 배치(JCG-2811). */}
              {isSignedIn && !scoreActive && isLearning && (
                <div ref={setAttendanceSlot} className="shrink-0" />
              )}
            </>
          )}
          </div>
          {/* Keep round statistics outside the tools/ad scroller so tall ads cannot push them offscreen. */}
          {roundStats && (
            <div data-article-round-stats className="shrink-0">
              <RoundStatsCard stats={roundStats} />
            </div>
          )}
        </div>
      );

  // 복원 도구(JCG-2102) — 복원 게시판에서만(isRestoration). 작성 진입 버튼·로그인 안내·통계·admin 을
  // RestorationPageClient 가 슬롯에 portal. 작성 모드(kind="compose")면 hidden 으로 보존.
  const restorationTools = isRestoration && (
        <div
          ref={setRestorationRightSlot}
          className={cn(
            "flex flex-1 flex-col gap-3 overflow-y-auto p-3",
            (rightColumn.kind === "compose" || panelsCollapsed) && "hidden",
          )}
        />
      );

  // 작성 모드 chrome(JCG-2102) — 셸이 헤더·닫기를 소유하고, PostEditor 를 composeSlot 에 portal.
  // 풀이 모드와 달리 오답노트 분할이 없어 단일 스크롤. 폭은 composeW 로 넓게 확장.
  // kind="compose" 일 때만 mount(compose ⟹ isRestoration 이므로 restoration 가드 불필요).
  const composePanel = (
        <div
          ref={composePanelRef}
          role="region"
          aria-label="복원 문제 작성"
          tabIndex={-1}
          onKeyDown={(e) => {
            // Escape → 작성 닫기(JCG-2123 #6). IME 조합 중·중첩 핸들러가 이미 막은 키는 무시.
            if (
              e.key === "Escape" &&
              !e.defaultPrevented &&
              !e.nativeEvent.isComposing
            ) {
              e.stopPropagation();
              exitCompose();
            }
          }}
          className="flex min-h-0 flex-1 flex-col focus:outline-none"
        >
          <div className="flex shrink-0 items-center justify-between px-4 pb-2.5 pt-3.5">
            <div className="flex items-center gap-2">
              <Pencil
                className="h-[18px] w-[18px] text-blue-500"
                aria-hidden="true"
              />
              <span className="text-[15px] font-extrabold text-foreground">
                복원 문제 작성
              </span>
            </div>
            <button
              type="button"
              onClick={exitCompose}
              title="작성 닫기"
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-card px-2 text-[12px] font-bold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              <X className="h-[15px] w-[15px]" aria-hidden="true" />
              닫기
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5">
            <div ref={setComposeSlot} className="min-h-0" />
          </div>
        </div>
  );

  // (풀이 모드 chrome `solutionAside` 는 custom 셸 분기와 공유하기 위해 위[tocSection 직전]에서
  //  정의했다 — JCG-2126. ForumProblemShell 과 동일 chrome 을 공유해 외관·동작 100% 일치.)

  // 우측 컬럼 외곽 prop(폭/리사이즈/배경)을 rightColumn.kind 단일 switch 로 도출(JCG-2141).
  // 불가능 상태(solution+compose 동시 등)는 union 의 단일 kind 로 이미 차단되고, default:never 로
  // 새 kind 누락을 컴파일타임에 잡는다. article 경로의 kind 는 compose/solution/panels 중 하나
  // (none 은 custom 셸 전용 — 위에서 early-return)지만, exhaustiveness 를 위해 none 도 명시한다.
  let rightWidthCss: string;
  let showRightResize: boolean;
  let rightAsideGray: boolean;
  switch (rightColumn.kind) {
    case "compose":
    case "solution":
    case "custom":
    case "panels":
      rightWidthCss = rightColumn.widthCss;
      showRightResize = rightColumn.showResize;
      rightAsideGray = rightColumn.gray;
      break;
    case "none":
      // 도달 불가(custom 셸 전용). 우측 미렌더라 값은 무시되지만 폴백으로 둔다.
      rightWidthCss = `${rightW}px`;
      showRightResize = true;
      rightAsideGray = false;
      break;
    default: {
      // exhaustiveness — 새 kind 추가 시 컴파일 에러로 분기 누락 차단.
      const _exhaustive: never = rightColumn;
      throw new Error(`unhandled right column kind: ${_exhaustive}`);
    }
  }

  // 우측 콘텐츠 — 4개 블록을 고정 순서(학습 도구 → 복원 도구 → 작성 패널 → 풀이)로 mount 한다.
  // **순서 고정이 회귀 가드의 핵심**: 모드 전환(panels↔solution↔compose, soft-nav) 시 블록의
  // 프래그먼트 인덱스가 바뀌면 React 가 위치 기반 재조정으로 slot ref(noteSlot/restorationRightSlot)
  // 를 remount 해 portal 상태·미저장 입력을 잃는다. 따라서 kind 별로 블록 집합을 재배치하지 않고,
  // 각 블록의 mount/hidden 여부만 rightColumn.kind(+isRestoration) 로 가른다(위 const 들이 담당):
  // - compose: restorationTools(hidden) + composePanel
  // - solution: learningPanels|restorationTools(현재 페이지) + solutionAside
  // - panels: learningPanels | restorationTools
  // - custom/none 은 variant="article" 경로에서 도달 불가(custom 셸은 위에서 early-return).
  const rightContent = (
    <>
      {learningPanels}
      {restorationTools}
      {rightRail}
      {rightColumn.kind === "compose" && composePanel}
      {rightColumn.kind === "solution" && solutionAside}
    </>
  );

  return (
    <SlotRegistryContext.Provider value={slotRegistry}>
     <SolutionModeContext.Provider value={solutionMode}>
      <ArticleVideoDockContext.Provider
        value={rightColumn.kind === "panels" ? videoDockSlot : null}
      >
      <ShellFrame
        columns="left+main+right"
        // 좌측 폭: 접힘 시 레일(58px) override, 그 외 leftW. resize 는 ArticleShell 소유(LS_LEFT).
        leftWidthCss={leftCollapsed ? `${RAIL_WIDTH}px` : `${leftW}px`}
        // 공개 시험 안내 목차와 starter+ 학습 도구의 좌측 폭을 조절한다.
        onResizeLeft={showLeftTools ? onResizeLeft : undefined}
        showLeftContent={!leftCollapsed}
        // 우측 폭/리사이즈/배경: 위 rightColumn switch 에서 파생(JCG-2141). Free 는 폭 고정(위 좌측 참조).
        rightWidthCss={panelsCollapsed ? `${SOLUTION_RAIL}px` : rightWidthCss}
        onResizeRight={onResizeRight}
        showRightResize={showTools && showRightResize && !panelsCollapsed}
        rightAsideClassName={
          rightAsideGray
            ? "bg-gray-50 dark:bg-gray-900/40"
            : isMockExamShell
              ? mockExamSidebarSurface
              : "bg-background"
        }
        // 본문(main) 배경: 파이널 모의고사만 오팔 상단 글로우(FINAL_MOCK_EXAM_MAIN_SURFACE, 넓은 읽기
        // 컬럼은 화이트 페이드로 확보). 그 외 라우트는 미전달(undefined) → ShellFrame base(bg-background) 유지.
        mainClassName={isFinalMockExam ? FINAL_MOCK_EXAM_MAIN_SURFACE : undefined}
        header={{
          leftCollapsed,
          onToggleSidebar: () => setLeftCollapsed((v) => !v),
          rightCollapsed,
          rightSidebarLabel,
          onToggleRightSidebar:
            showTools && rightColumn.kind === "panels"
              ? () => setRightDefault(!rightCollapsed)
              : undefined,
          categoryLabel,
          scopeLabel,
          headerSlotRef: setShellHeaderSlot,
          headerActionSlotRef: setHeaderActionSlot,
          // 파이널 모의고사만 헤더에 은은한 앰버 틴트 주입 — 그 외 라우트는 undefined 로 기본 유지(JCG-2898).
          surfaceClassName: isFinalMockExam ? FINAL_MOCK_EXAM_HEADER_SURFACE : undefined,
        }}
        left={leftContent}
        right={rightContent}
      >
        <div className="mx-auto w-full max-w-[820px] px-4 pb-20 pt-2 sm:px-8">
          {children}
          {footer}
        </div>
      </ShellFrame>

      {globals}
      </ArticleVideoDockContext.Provider>
     </SolutionModeContext.Provider>
    </SlotRegistryContext.Provider>
  );
}
